import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { ShoppingBag, Users, BarChart3, Star, Shield, Zap, TrendingUp, Clock } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';

interface AboutPageProps {
  onNavigate?: (page: string) => void;
}

export function AboutPage({ onNavigate }: AboutPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });

  const features = [
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Real-time insights into your restaurant performance with detailed charts and reports.',
      color: 'bg-blue-500',
    },
    {
      icon: ShoppingBag,
      title: 'Order Management',
      description: 'Efficiently track and manage all orders from placement to delivery.',
      color: 'bg-green-500',
    },
    {
      icon: Users,
      title: 'Customer Insights',
      description: 'Understand your customers better with detailed profiles and behavioral analytics.',
      color: 'bg-purple-500',
    },
    {
      icon: Star,
      title: 'Review Monitoring',
      description: 'Track customer feedback and respond to reviews to maintain your reputation.',
      color: 'bg-yellow-500',
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with 99.9% uptime to keep your business running.',
      color: 'bg-red-500',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized performance ensures quick response times for all operations.',
      color: 'bg-orange-500',
    },
  ];

  const stats = [
    { label: 'Active Users', value: '10,000+', icon: Users },
    { label: 'Orders Processed', value: '500K+', icon: ShoppingBag },
    { label: 'Customer Satisfaction', value: '98%', icon: Star },
    { label: 'Uptime', value: '99.9%', icon: TrendingUp },
  ];

  const team = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
    },
    {
      name: 'Michael Chen',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Product',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
    },
    {
      name: 'David Kim',
      role: 'Lead Designer',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="about"
        onNavigate={onNavigate}
        ordersCount={ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen} 
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto">
          {/* Hero Section */}
          <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 py-8 px-6">
            <div className="max-w-[1400px] mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">Qrypt Dashboard</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Empowering restaurant owners with powerful tools to manage, analyze, and grow their business efficiently.
              </p>
            </div>
          </div>

          <div className="max-w-[1400px] mx-auto px-6 py-12">
            {/* Mission Section */}
            <div className="mb-16">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Our Mission</h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-4">
                  At Qrypt, we believe that running a successful restaurant should be simple and intuitive. Our mission is to provide restaurant owners with a comprehensive platform that streamlines operations, enhances customer experiences, and drives business growth.
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed">
                  We're committed to continuous innovation, ensuring our platform evolves with your needs and stays ahead of industry trends.
                </p>
              </div>
            </div>

            {/* Stats Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Impact</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center hover:shadow-lg transition-shadow"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full mb-4">
                      <stat.icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Key Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 hover:shadow-lg transition-all hover:-translate-y-1"
                  >
                    <div className={`inline-flex items-center justify-center w-12 h-12 ${feature.color} rounded-lg mb-4`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Team Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Meet Our Team</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {team.map((member, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1"
                  >
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-gray-100 dark:border-gray-800"
                    />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{member.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{member.role}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Journey</h2>
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-8">
                <div className="space-y-8">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div className="w-1 h-full bg-indigo-200 dark:bg-indigo-900 mt-2"></div>
                    </div>
                    <div className="flex-1 pb-8">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">2023 - Launch</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Qrypt POS Dashboard was launched to help restaurant owners streamline their operations.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        <Users className="w-6 h-6" />
                      </div>
                      <div className="w-1 h-full bg-purple-200 dark:bg-purple-900 mt-2"></div>
                    </div>
                    <div className="flex-1 pb-8">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">2024 - 1,000 Customers</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Reached our first milestone of 1,000 active restaurant partners across the country.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                        <Star className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">2025 - AI Integration</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Introduced AI-powered analytics and predictive insights to help restaurants make data-driven decisions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Restaurant?</h2>
              <p className="text-indigo-100 mb-6 text-lg">
                Join thousands of restaurant owners who trust Qrypt to manage their business.
              </p>
              <button className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors">
                Get Started Today
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}